import React, { useState, useCallback } from "react";
import { RotateCcw, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { useChatContext } from "../ChatContext";
import { useWorkflow } from "../../context/WorkflowContext";
import { useWorkflowGeneration } from "../../context/WorkflowGenerationContext";

export default function RollbackButton({ messageId }) {
    const { t } = useTranslation();
    const [isRollingBack, setIsRollingBack] = useState(false);
    const {
        setPrefillMessage,
        confirm,
        selectedEnvironment,
        requestPreviewReload,
    } = useChatContext();
    const { refetchWorkflow } = useWorkflow();
    const { activeStep, fetchStepChatMessages, setMissingSecretWarnings, resyncStepsConfig } =
        useWorkflowGeneration();

    const { call: rollbackCall } = useBluelagoon({
        method: "rollbackStepChat",
    });

    const handleRollback = useCallback(async () => {
        if (isRollingBack) return;
        try {
            await confirm({
                title: t("chatMessages.revertTitle", "Revert to this message"),
                description: t(
                    "chatMessages.revertDescription",
                    "This will restore the app to the version at this message and delete all messages after it. This action cannot be undone."
                ),
            });
        } catch {
            return;
        }
        setIsRollingBack(true);
        try {
            const result = await rollbackCall({
                messageId,
                environmentName: selectedEnvironment || "production",
            });
            if (result?.status === 200) {
                if (result.rolledBackContent) {
                    setPrefillMessage(result.rolledBackContent);
                }
                setMissingSecretWarnings(
                    result.missingSecretWarnings || []
                );
                await Promise.all([
                    refetchWorkflow(),
                    activeStep ? fetchStepChatMessages(activeStep) : Promise.resolve(),
                ]);
                resyncStepsConfig();
                // Production rollback flips isActive on an existing
                // WorkflowStepVersion without changing its number, so the
                // iframe's key (which is keyed off currentVersion) doesn't
                // change on its own. Force a remount so the preview reflects
                // the restored components.
                requestPreviewReload?.();
            }
        } catch (err) {
            console.error("Rollback failed:", err);
        } finally {
            setIsRollingBack(false);
        }
    }, [messageId, isRollingBack, rollbackCall, setPrefillMessage, setMissingSecretWarnings, refetchWorkflow, activeStep, fetchStepChatMessages, resyncStepsConfig, confirm, selectedEnvironment, requestPreviewReload, t]);

    return (
        <button
            className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-blue-600 transition-colors"
            onClick={handleRollback}
            disabled={isRollingBack}
        >
            {isRollingBack ? (
                <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
                <RotateCcw className="h-3 w-3" />
            )}
            <span>{t("chatMessages.revertThis", "Revert")}</span>
        </button>
    );
}
